import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { axiosInstance } from "@retro-ia/shared/axios";
import type {
  ClienteFinal,
  ClienteFinalProgramas,
  Aprendiz,
} from "@interfaces/user.interface";

export interface ClientesResponse {
  data: ClienteFinal[];
  total: number;
}

export interface ClienteFinalProgramasResponse {
  data: ClienteFinalProgramas[];
  total: number;
}

export interface ClienteFinalAprendiz {
  programaId: number;
  programaNombre: string;
}

export interface ClienteFinalAprendicesResponse {
  data: Aprendiz[];
  total: number;
  page: number;
  limit: number;
}

export const useClientesFinales = (
  page: number,
  limit: number,
  search: string
) => {
  return useQuery<ClientesResponse, Error>({
    queryKey: ["clientesFinales", page, limit, search],
    queryFn: async () => {
      const response = await axiosInstance.get("/clientes-finales", {
        params: { page, limit, search },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useMisClientesFinales = (
  page: number,
  limit: number,
  search: string
) => {
  return useQuery<ClientesResponse, Error>({
    queryKey: ["misClientesFinales", search, page, limit],
    queryFn: async () => {
      const response = await axiosInstance.get(
        "/clientes-finales/usuario-alta",
        {
          params: { page, limit, search },
        }
      );
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useClientesFinalesProgramas = () => {
  return useQuery<ClienteFinalProgramasResponse, Error>({
    queryKey: ["clientesFinalesProgramas"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        "/clientes-finales/mis-clientes-finales-programas"
      );
      return response.data;
    },
    staleTime: 0, // Sin cache temporalmente para depuración
  });
};

export const useClienteFinalAprendices = (
  idClienteFinal: number,
  page: number = 1,
  limit: number = 10,
  programaId?: number,
  search: string = ""
) => {
  return useQuery<ClienteFinalAprendicesResponse, Error>({
    queryKey: [
      "clienteFinalAprendices",
      idClienteFinal,
      page,
      limit,
      programaId,
      search,
    ],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `/clientes-finales/${idClienteFinal}/aprendices`,
        {
          params: {
            page,
            limit,
            ...(programaId ? { idPrograma: programaId } : {}),
            ...(search ? { search } : {}),
          },
        }
      );
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
};
